import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {FormattedMessage} from 'react-intl';
import {Link} from 'react-router-dom';
import messages from '../../../res/translations/messages';
import {fetchMediaUsage, renameMedia} from '../../services/api';
import TooltipContainer from '../TooltipContainer';
import Icon from '../Icon';
import Loader from '../Loader';
import TruncatedModalTitle from '../TruncatedModalTitle';

const FILENAME_MAX_LENGTH = 128;

class MediaAssetCard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            deleteModalOpen: false, usageModalOpen: false, usage: null, usageLoading: false,
            zoomModalOpen: false,
            filename: props.item.filename,
            isEditingName: false,
            nameDraft: props.item.filename,
            savingName: false,
            nameError: null,
        };
        this.toggleDeleteModal = this.toggleDeleteModal.bind(this);
        this.toggleUsageModal = this.toggleUsageModal.bind(this);
        this.toggleZoomModal = this.toggleZoomModal.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.startEditingName = this.startEditingName.bind(this);
        this.cancelEditingName = this.cancelEditingName.bind(this);
        this.handleNameChange = this.handleNameChange.bind(this);
        this.handleNameKeyDown = this.handleNameKeyDown.bind(this);
        this.saveName = this.saveName.bind(this);
    }

    startEditingName() {
        this.setState({isEditingName: true, nameDraft: this.state.filename, nameError: null});
    }

    cancelEditingName() {
        this.setState({isEditingName: false, nameError: null});
    }

    handleNameChange(e) {
        this.setState({nameDraft: e.target.value});
    }

    handleNameKeyDown(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.saveName();
        } else if (e.key === 'Escape') {
            this.cancelEditingName();
        }
    }

    async saveName() {
        if (!this.state.isEditingName || this.state.savingName) {
            return;
        }

        const filename = this.state.nameDraft.trim();
        if (!filename) {
            this.setState({nameError: messages.media.errors.renameEmpty});
            return;
        }
        if (filename === this.state.filename) {
            this.setState({isEditingName: false, nameError: null});
            return;
        }

        this.setState({savingName: true, nameError: null});
        try {
            const response = await renameMedia(this.props.item.id, filename);
            this.setState({filename: response.data.filename, isEditingName: false, savingName: false});
        } catch (error) {
            this.setState({savingName: false, nameError: messages.media.errors.renameFailed});
        }
    }

    toggleDeleteModal() {
        this.setState((state) => ({deleteModalOpen: !state.deleteModalOpen}));
    }

    toggleZoomModal() {
        this.setState((state) => ({zoomModalOpen: !state.zoomModalOpen}));
    }

    async toggleUsageModal() {
        const opening = !this.state.usageModalOpen;
        this.setState({usageModalOpen: opening});
        if (opening) {
            this.setState({usageLoading: true});
            const response = await fetchMediaUsage(this.props.item.id);
            this.setState({usage: response.data.surveys, usageLoading: false});
        }
    }

    handleDelete() {
        this.props.onDelete(this.props.item.id);
    }

    render() {
        const {item, canEdit} = this.props;
        const {
            deleteModalOpen, usageModalOpen, zoomModalOpen, usage, usageLoading,
            filename, isEditingName, nameDraft, savingName, nameError,
        } = this.state;
        const canDelete = canEdit && !item.inUse;

        return (
            <div className="col-6 col-md-4 col-lg-3 mb-4">
                <div className="card h-100">
                    <div className="position-relative">
                        <img src={item.thumbUrl || item.url} className="card-img-top" alt={filename} style={{objectFit: 'cover', height: 150}} />
                        <div className="position-absolute d-flex flex-column" style={{top: 8, left: 8}}>
                            <TooltipContainer tooltip={messages.media.tooltips.zoom}>
                                <a onClick={this.toggleZoomModal}
                                    className="btn btn-light rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0 mb-2"
                                    style={{width: 24, height: 24}}>
                                    <Icon icon="search" style={{fontSize: 12}} />
                                </a>
                            </TooltipContainer>
                            <TooltipContainer tooltip={canDelete ? messages.media.tooltips.delete : messages.media.tooltips.deleteDisabled}>
                                <a onClick={canDelete ? this.toggleDeleteModal : undefined}
                                    className={classNames(
                                        'btn btn-light rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0',
                                        {disabled: !canDelete},
                                    )}
                                    style={{width: 24, height: 24}}>
                                    <Icon icon="trash" type={canDelete ? 'danger' : undefined} style={{fontSize: 12}} />
                                </a>
                            </TooltipContainer>
                        </div>
                    </div>
                    <div className="card-body p-2">
                        {isEditingName ? (
                            <div className="mb-2">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={nameDraft}
                                    maxLength={FILENAME_MAX_LENGTH}
                                    autoFocus
                                    disabled={savingName}
                                    onChange={this.handleNameChange}
                                    onKeyDown={this.handleNameKeyDown}
                                    onBlur={this.saveName}
                                />
                                {nameError && <small className="text-danger"><FormattedMessage {...nameError} /></small>}
                            </div>
                        ) : (
                            <p className="card-text d-flex align-items-center mb-2">
                                <span className="text-truncate" title={filename} style={{minWidth: 0}}>{filename}</span>
                                {canEdit && (
                                    <TooltipContainer tooltip={messages.media.tooltips.rename}>
                                        <a onClick={this.startEditingName} className="ml-2 flex-shrink-0">
                                            <Icon icon="edit" />
                                        </a>
                                    </TooltipContainer>
                                )}
                            </p>
                        )}
                        {item.inUse && (
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="badge badge-info">
                                    <FormattedMessage {...messages.media.card.inUse} />
                                </span>
                                <TooltipContainer tooltip={messages.media.tooltips.viewUsage}>
                                    <a onClick={this.toggleUsageModal} className="ml-2 flex-shrink-0">
                                        <Icon icon="view" />
                                    </a>
                                </TooltipContainer>
                            </div>
                        )}
                    </div>
                </div>

                <Modal isOpen={deleteModalOpen} toggle={this.toggleDeleteModal}>
                    <ModalHeader toggle={this.toggleDeleteModal} tag={TruncatedModalTitle}>
                        <span title={filename}>
                            <FormattedMessage {...messages.media.modals.remove.title} values={{name: filename}} />
                        </span>
                    </ModalHeader>
                    <ModalBody>
                        <FormattedMessage {...messages.media.modals.remove.body} />
                    </ModalBody>
                    <ModalFooter>
                        <button className="btn btn-outline-secondary mr-auto" type="button" onClick={this.toggleDeleteModal}>
                            <FormattedMessage {...messages.buttons.cancel} />
                        </button>
                        <button className="btn btn-primary" type="button" onClick={this.handleDelete}>
                            <Icon icon="trash" /><FormattedMessage {...messages.surveys.button.remove} />
                        </button>
                    </ModalFooter>
                </Modal>

                <Modal isOpen={usageModalOpen} toggle={this.toggleUsageModal}>
                    <ModalHeader toggle={this.toggleUsageModal} tag={TruncatedModalTitle}>
                        <span title={filename}>
                            <FormattedMessage {...messages.media.modals.usage.title} values={{name: filename}} />
                        </span>
                    </ModalHeader>
                    <ModalBody>
                        {usageLoading ? <Loader /> : (
                            (usage && usage.length > 0) ? (
                                <ul className="list-unstyled mb-0">
                                    {usage.map((survey) => (
                                        <li key={survey.id}>
                                            <Link to={`/surveys/${survey.id}/edit`}>{survey.name}</Link>
                                        </li>
                                    ))}
                                </ul>
                            ) : <FormattedMessage {...messages.media.modals.usage.empty} />
                        )}
                    </ModalBody>
                </Modal>

                <Modal isOpen={zoomModalOpen} toggle={this.toggleZoomModal} size="lg">
                    <ModalHeader toggle={this.toggleZoomModal} tag={TruncatedModalTitle}>
                        <span title={filename}>{filename}</span>
                    </ModalHeader>
                    <ModalBody className="text-center">
                        <img src={item.url} alt={filename} style={{maxWidth: '100%', maxHeight: '80vh'}} />
                    </ModalBody>
                </Modal>
            </div>
        );
    }
}

MediaAssetCard.propTypes = {
    item: PropTypes.object.isRequired,
    canEdit: PropTypes.bool.isRequired,
    onDelete: PropTypes.func.isRequired,
};

export default MediaAssetCard;
